import {
  ReviewForgeElementType,
  StaticElementType,
} from "@redotech/redo-model/forge/review-forge-types";
import { isEnumValue } from "@redotech/util/enum";
import { memo, useContext } from "react";
import { RedoButton } from "../arbiter-components/buttons/redo-button";
import ArrowLeftIcon from "../arbiter-icon/arrow-left_filled.svg";
import ArrowRightIcon from "../arbiter-icon/arrow-right_filled.svg";
import PlusCircleIcon from "../arbiter-icon/plus-circle.svg";
import { BrandKitButton } from "../brand-kit-button";
import { Flex } from "../flex";
import { FormElementDrop } from "../form-builder/form-element-drop";
// import { DisabledTooltip } from "../tooltip/disabled-tooltip";
import {
  BrandKitContext,
  ReviewFormSelectionContext,
  SetReviewFormSelectionContext,
} from "./review-form-editor-contexts";
import { ReviewFormPageMenuBar } from "./review-form-menu-bar";
import { ReviewFormPageElement } from "./review-form-page-element";
import * as reviewFormPreviewCss from "./review-form-preview.module.css";
import {
  getDefaultElement,
  getRichText,
  getSemanticTagElement,
  PageForm,
  PagesForm,
  ReviewForgeDroppableItem,
  ReviewForgeDroppableItems,
  ThankYouPageForm,
} from "./review-form-types";
import { ReviewThankYouUpsellPage } from "./review-thank-you-upsell-page";

export const ReviewsFormPreview = memo(function ReviewsFormPreview({
  pages,
  thankYouPage,
}: {
  pages: PagesForm;
  thankYouPage: ThankYouPageForm;
}) {
  const totalPages = pages.value.length;

  const brandKit = useContext(BrandKitContext);
  const setReviewFormSelection = useContext(SetReviewFormSelectionContext);

  return (
    <Flex
      bgColor="primary"
      className={reviewFormPreviewCss.previewSection}
      dir="column"
      flex={3.5}
      gap="none"
      w="full"
    >
      <Flex
        className={reviewFormPreviewCss.previewFlex}
        dir="column"
        gap="6xl"
        px="5xl"
        py="5xl"
      >
        {pages.inputs.map((page, index) => (
          <ReviewFormPageSection
            index={index}
            key={index}
            maxIndex={totalPages - 1}
            page={page}
          />
        ))}
        <RedoButton
          className={reviewFormPreviewCss.addPageButton}
          IconLeading={PlusCircleIcon}
          onClick={() => {
            const newPages = [
              ...pages.value,
              {
                elements: [
                  getRichText(brandKit, { html: "<p>Page title</p>" }),
                ],
              },
            ];
            pages.setValue(newPages);
            setReviewFormSelection({
              selectedPageIndex: newPages.length - 1,
              selectedElementIndex: null,
            });
          }}
          size="xl"
        />
        <ThankYouPageSelectionWrapper thankYouPage={thankYouPage} />
      </Flex>
    </Flex>
  );
});

const ReviewFormPageSection = memo(function ReviewFormPageSection({
  page,
  index,
  maxIndex,
}: {
  page: PageForm;
  index: number;
  maxIndex: number;
}) {
  const selectedElement = useContext(ReviewFormSelectionContext);
  const pageSelected =
    selectedElement?.selectedPageIndex === index &&
    selectedElement?.selectedElementIndex === null;

  return (
    <Flex gap="xl" w="full">
      <ReviewsFormPage index={index} maxIndex={maxIndex} page={page} />
      {pageSelected ? (
        <ReviewFormPageMenuBar maxPageIndex={maxIndex} pageIndex={index} />
      ) : (
        <div className={reviewFormPreviewCss.menuPlaceholder} />
      )}
    </Flex>
  );
});

const ReviewsFormPage = memo(function ReviewsFormPage<T>({
  page,
  index,
  maxIndex,
}: {
  page: PageForm;
  index: number;
  maxIndex: number;
}) {
  const selectedElement = useContext(ReviewFormSelectionContext);
  const setSelectedElement = useContext(SetReviewFormSelectionContext);

  const brandKit = useContext(BrandKitContext);

  function handleElementDrop(
    element: ReviewForgeElementType | string,
    index: number,
  ) {
    const newElement = generateNewElement(element);

    const elements = page.value.elements;
    const newElements = elements
      .slice(0, index + 1)
      .concat(newElement as any)
      .concat(elements.slice(index + 1));
    page.setValue({ elements: newElements });
  }

  function generateNewElement(element: ReviewForgeElementType | string) {
    if (isEnumValue(element, StaticElementType)) {
      return getDefaultElement(element, brandKit);
    } else {
      return getSemanticTagElement(element);
    }
  }

  return (
    <Flex
      className={reviewFormPreviewCss.formPageWrapper}
      dir="column"
      gap="none"
      justify="space-between"
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          setSelectedElement({
            selectedPageIndex: index,
            selectedElementIndex: null,
          });
        }
      }}
      pb="4xl"
      pt="2xl"
      px="7xl"
      style={{ backgroundColor: brandKit.colors.background }}
    >
      <FormElementDrop<ReviewForgeDroppableItem>
        elementDropped={(element) => {
          handleElementDrop(element, -1);
        }}
        possibleValues={ReviewForgeDroppableItems}
      />
      <Flex dir="column" gap="3xl">
        {page.value.elements.map((element, elementIndex) => {
          const isSelected =
            selectedElement.selectedPageIndex === index &&
            selectedElement.selectedElementIndex === elementIndex;

          return (
            <Flex dir="column" gap="none" key={elementIndex}>
              <ReviewFormPageElement
                elementIndex={elementIndex}
                isSelected={isSelected}
                props={{ element, pageIndex: index, page }}
                selectElement={() => {
                  setSelectedElement({
                    selectedPageIndex: index,
                    selectedElementIndex: elementIndex,
                  });
                }}
              />
              <FormElementDrop<ReviewForgeDroppableItem>
                elementDropped={(element) => {
                  handleElementDrop(element, elementIndex);
                }}
                possibleValues={ReviewForgeDroppableItems}
              />
            </Flex>
          );
        })}
      </Flex>
      <PaginationButtons
        handleNextClick={() => {}}
        handlePreviousClick={() => {}}
        index={index}
        maxIndex={maxIndex}
        pending={false}
        submit={() => {}}
      />
    </Flex>
  );
});

const ThankYouPageSelectionWrapper = memo(
  function ThankYouPageSelectionWrapper({
    thankYouPage,
  }: {
    thankYouPage: ThankYouPageForm;
  }) {
    const brandKit = useContext(BrandKitContext);
    return (
      <Flex gap="xl" w="full">
        <Flex
          className={reviewFormPreviewCss.formPageWrapper}
          dir="column"
          gap="none"
          justify="space-between"
          pb="4xl"
          pt="2xl"
          px="7xl"
          style={{ backgroundColor: brandKit.colors.background }}
        >
          <ReviewThankYouUpsellPage thankYouPage={thankYouPage} />
        </Flex>
      </Flex>
    );
  },
);

export const PaginationButtons = memo(function PaginationButtons({
  index,
  maxIndex,
  handlePreviousClick,
  handleNextClick,
  pending,
  submit,
}: {
  index: number;
  maxIndex: number;
  handlePreviousClick: () => void;
  handleNextClick: () => void;
  submit: () => void;
  pending: boolean;
}) {
  const brandKit = useContext(BrandKitContext);

  // REVIEWS-TODO the disabled tooltip renders behind the modal on the storefront
  // const disabledMessage = disableNext
  //   ? "Please fill out all fields before moving on"
  //   : false;

  return (
    <Flex className={reviewFormPreviewCss.pagination}>
      <Flex grow="1" justify="space-between" pt="3xl">
        <div>
          {index !== 0 && (
            <BrandKitButton
              brandKit={brandKit}
              disabled={index === 0}
              hierarchy="tertiary"
              IconLeading={ArrowLeftIcon}
              onClick={handlePreviousClick}
              size="lg"
              text="Previous"
            />
          )}
        </div>
        <div>
          {/* <DisabledTooltip disabledMessage={disabledMessage}> */}
          {index !== maxIndex ? (
            <BrandKitButton
              brandKit={brandKit}
              hierarchy="tertiary"
              IconLeading={ArrowRightIcon}
              onClick={handleNextClick}
              size="lg"
              text="Next"
            />
          ) : (
            <BrandKitButton
              brandKit={brandKit}
              hierarchy="primary"
              onClick={submit}
              pending={pending}
              size="lg"
              text="Submit"
            />
          )}
          {/* </DisabledTooltip> */}
        </div>
      </Flex>
    </Flex>
  );
});
